import { useState, useEffect } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Collection } from '@/types/models'

interface PreviewImage {
  url: string
  id: string
}

// Simple in-memory cache for collection preview images
const imageCache = new Map<string, PreviewImage[]>()

interface CollectionPreviewGridProps {
  collection: Collection
  size?: 'sm' | 'md' // sm for 12x12, md for larger
}

export function CollectionPreviewGrid({ collection, size = 'sm' }: CollectionPreviewGridProps) {
  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([])
  const [loading, setLoading] = useState(true)

  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16'
  }

  useEffect(() => {
    async function fetchPreviewImages() {
      // Check cache first
      const cacheKey = collection.id
      if (imageCache.has(cacheKey)) {
        setPreviewImages(imageCache.get(cacheKey)!)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        
        // Get first 4 items from the collection
        const itemsToFetch = collection.items.slice(0, 4)
        
        const imagePromises = itemsToFetch.map(async (item) => {
          const collectionName = item.type === 'flash' ? 'flashes' : 'portfolioItems'
          const itemRef = doc(db, collectionName, item.id)
          const itemDoc = await getDoc(itemRef)
          
          if (itemDoc.exists()) {
            const data = itemDoc.data()
            return {
              url: data.compressedImageUrl || data.imageUrl,
              id: item.id
            }
          }
          
          return null
        })
        
        const images = await Promise.all(imagePromises)
        const filteredImages = images.filter(Boolean) as PreviewImage[]
        
        // Store in cache
        imageCache.set(cacheKey, filteredImages)
        
        setPreviewImages(filteredImages)
        setLoading(false)
      } catch (err) {
        console.error('Error fetching preview images:', err)
        setLoading(false)
      }
    }

    if (collection.items.length > 0) {
      fetchPreviewImages()
    } else {
      setLoading(false)
    }
  }, [collection.id, collection.items])

  // Determine layout: single image for 3 or fewer items, grid for more
  const isSingleImage = previewImages.length <= 3

  return (
    <div className={`rounded-lg overflow-hidden bg-muted flex-shrink-0 ${sizeClasses[size]}`}>
      {loading ? (
        // Skeleton loader
        <div className="w-full h-full bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-pulse" />
      ) : collection.coverImageUrl ? (
        // Cover image
        <div className="w-full h-full">
          <img
            src={collection.coverImageUrl}
            alt={`${collection.title} cover`}
            className="w-full h-full object-cover select-none"
            draggable="false"
          />
        </div>
      ) : previewImages.length === 0 ? (
        // Empty state
        <div className="w-full h-full flex items-center justify-center">
          <p className="text-xs font-medium text-muted-foreground">Empty</p>
        </div>
      ) : isSingleImage ? (
        // Single image layout for 3 or fewer items
        <div className="w-full h-full">
          <img 
            src={previewImages[0].url} 
            alt="Preview"
            className="w-full h-full object-cover select-none"
            draggable="false"
          />
        </div>
      ) : (
        // 2x2 grid layout for more than 3 items - no gaps, clean edges
        <div className="w-full h-full grid grid-cols-2 grid-rows-2">
          {previewImages.slice(0, 4).map((image, index) => (
            <div 
              key={image.id} 
              className="relative aspect-square overflow-hidden bg-background"
            >
              <img 
                src={image.url} 
                alt={`Preview ${index + 1}`}
                className="w-full h-full object-cover select-none"
                draggable="false"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


